// Resolución de la pestaña pedida en la ficha del cliente.
// Si el plan no incluye esa capacidad, no se cae en silencio a General.
//
// Chatbot, embudos (etapas) y estructura del sitio son pestañas propias.
// La configuración profunda no vive en /settings del área del cliente.

export const DETAIL_TABS = [
  'general',
  'implementacion',
  'correo',
  'avisos',
  'chatbot',
  'embudos',
  'estructura',
] as const;

export type DetailTabId = (typeof DETAIL_TABS)[number];

/** URLs viejas (?tab=pipelines, schemas, …) abren la pestaña nueva. */
export const DETAIL_TAB_ALIASES = {
  pipelines: 'embudos',
  etapas: 'embudos',
  schemas: 'estructura',
} as const satisfies Record<string, DetailTabId>;

/** URLs viejas que siguen siendo un ancla dentro de una pestaña. */
export const LEGACY_DETAIL_SECTIONS = {
  compartir: 'instalar',
  integraciones: 'integraciones',
  sla: 'sla',
} as const;

const LEGACY_SECTION_TAB = {
  compartir: 'implementacion',
  integraciones: 'implementacion',
  sla: 'embudos',
} as const satisfies Record<keyof typeof LEGACY_DETAIL_SECTIONS, DetailTabId>;

export type DetailSectionId =
  | (typeof LEGACY_DETAIL_SECTIONS)[keyof typeof LEGACY_DETAIL_SECTIONS]
  | 'cms';

export interface DetailTabDef {
  id: DetailTabId;
  label: string;
  show: boolean;
}

export interface ResolvedDetailTab {
  activeId: DetailTabId;
  /** Ancla dentro de la pestaña activa. null si la pestaña no tiene sección. */
  section: DetailSectionId | null;
  /** Pestaña pedida que este cliente no tiene en su plan. */
  missing: { id: DetailTabId; label: string } | null;
}

export function resolveDetailTab(
  requested: string | null | undefined,
  tabs: readonly DetailTabDef[],
): ResolvedDetailTab {
  const alias = requested
    ? DETAIL_TAB_ALIASES[requested as keyof typeof DETAIL_TAB_ALIASES]
    : undefined;
  const id = alias ?? requested;
  const legacy = id
    ? LEGACY_DETAIL_SECTIONS[id as keyof typeof LEGACY_DETAIL_SECTIONS]
    : undefined;
  if (legacy && id && id in LEGACY_SECTION_TAB) {
    return {
      activeId: LEGACY_SECTION_TAB[id as keyof typeof LEGACY_SECTION_TAB],
      section: legacy,
      missing: null,
    };
  }
  const known = tabs.find((t) => t.id === id);
  if (known?.show) return { activeId: known.id, section: null, missing: null };
  if (known && !known.show) {
    return {
      activeId: 'general',
      section: null,
      missing: { id: known.id, label: known.label },
    };
  }
  return { activeId: 'general', section: null, missing: null };
}

export function missingTabMessage(label: string): string {
  return `Este cliente no tiene ${label} en su plan.`;
}

export const ACTIVATE_CAPABILITY_LABEL = 'Activar en General → Capacidades';
